import type { IErrorToastContext } from "@/app/_utils/context/ErrorToastContext";
import handleRequest, {
  type IHandleRequestConfig,
} from "@/app/_utils/functions/handleRequest";
import { useCallback, useRef } from "react";

export type TRequestConfig<T> = Omit<
  IHandleRequestConfig<T>,
  "abortController" | "setToastError"
> & {
  "requestSlug": string;
  "returnPromise"?: boolean;
};

/**
 * Custom hook for managing requests with automatic abort controller handling.
 *
 * @param setToastError - Function to display error toast messages.
 *
 * @returns Object containing request management functions.
 */
export function useRequest(
  setToastError: IErrorToastContext["setToastError"]
): {
  "makeRequest": {
    <T>(config: TRequestConfig<T> & { returnPromise: true }): Promise<T>;
    <T>(config: TRequestConfig<T>): undefined;
  };
  "abortRequestBySlug": (slug: string) => boolean;
  "abortAllRequests": () => void;
  "getActiveRequestSlugs": () => string[];
} {
  // Track active requests with their identifiers:
  const activeRequestsRef = useRef<Map<string, AbortController>>(new Map());

  // Function to make requests with automatic abort controller management:
  const makeRequest = useCallback(
    <T>(config: TRequestConfig<T>): Promise<T> | undefined => {
      const promise = new Promise<T>((resolve, reject) => {
        const abortController = new AbortController();
        const { requestSlug } = config;

        // Abort any existing request with the same slug before starting a new one:
        const existing = activeRequestsRef.current.get(requestSlug);
        if (existing) {
          existing.abort();
        }

        activeRequestsRef.current.set(requestSlug, abortController);
        const originalCleanup = config.cleanup;
        const originalCallback = config.callback;
        const originalSetCustomErr = config.setCustomErr;

        // Remove returnPromise from the config we pass to handleRequest:
        const {
          returnPromise: _,
          ...handleRequestConfig
        } = config;

        let promiseRejected = false;

        // Call handleRequest with our managed abort controller:
        handleRequest<T>({
          ...handleRequestConfig,
          abortController,
          setToastError: (error: unknown) => {
            const errorMsg =
              typeof error === "string" || error === null
                ? error
                : String(error);

            if (errorMsg !== null && !promiseRejected) {
              promiseRejected = true;
              reject(new Error(errorMsg));
            }

            // Only forward actual error messages to the outer handler.
            // handleRequest calls setToastError(null) on every success to
            // clear a previous toast — that null must not surface as an
            // error event to the caller's error handler.
            if (errorMsg !== null) {
              setToastError(errorMsg);
            }
          },
          setCustomErr: originalSetCustomErr
            ? (error: string): void => {
                if (!promiseRejected) {
                  promiseRejected = true;
                  reject(new Error(error));
                }

                originalSetCustomErr(error);
              }
            : undefined,
          callback: (data: T) => {
            if (originalCallback) {
              originalCallback(data);
            }

            resolve(data);
          },
          cleanup: () => {
            activeRequestsRef.current.delete(requestSlug);

            if (originalCleanup) {
              originalCleanup();
            }
          },
        });
      });

      if (config.returnPromise === true) {
        return promise;
      }

      // Suppress unhandled-rejection noise — errors are already surfaced to
      // the user via setCustomErr (toast) or setToastError before this point.
      promise.catch(() => {
        // error already handled by setCustomErr or setToastError
      });

      return undefined;
    },
    [setToastError]
  );

  // Function to abort a specific request by slug:
  const abortRequestBySlug = (slug: string): boolean => {
    const controller = activeRequestsRef.current.get(slug);

    if (controller) {
      controller.abort();
      activeRequestsRef.current.delete(slug);

      return true;
    }

    return false;
  };

  // Function to abort all active requests:
  const abortAllRequests = useCallback((): void => {
    for (const controller of activeRequestsRef.current.values()) {
      controller.abort();
    }

    activeRequestsRef.current.clear();
  }, []);

  // Function to get all active request slugs:
  const getActiveRequestSlugs = (): string[] => {
    return Array.from(activeRequestsRef.current.keys());
  };

  return {
    makeRequest: makeRequest as {
      <T>(config: TRequestConfig<T> & { returnPromise: true }): Promise<T>;
      <T>(config: TRequestConfig<T>): undefined;
    },
    abortRequestBySlug,
    abortAllRequests,
    getActiveRequestSlugs,
  };
}
